/**
 * Which cases the Cases list, Dashboard and Recently Viewed treat as
 * "artifacts" (demo / test / sandbox leftovers) when the officer turns the
 * artifact filter on.
 *
 * The filter is OFF by default and must never hide a real case silently:
 *   - only an explicit marker (`is_artifact`, or an artifact tag) or a whole
 *     word "test" / "demo" / "fake" at the very START of the title qualifies;
 *   - substrings inside words ("Latest", "Contest", "Demonetisation") and the
 *     FIR number or officer name never count;
 *   - a case the current user owns, or has viewed in the last 30 days, is
 *     never an artifact, whatever its title says.
 * Hidden cases stay reachable: callers render them as a collapsed group.
 */

const ARTIFACT_TITLE_START = /^[^\p{L}\p{N}]*(test|demo|fake)(?![\p{L}\p{N}_])/iu;
const ARTIFACT_TAGS = new Set(['artifact', 'test', 'demo', 'fake']);

export const RECENT_VIEW_WINDOW_DAYS = 30;
export const HIDE_ARTIFACTS_STORAGE_KEY = 'cases.hideArtifacts';

export type CaseLike = {
  id?: string | number | null;
  title?: string | null;
  fir_number?: string | null;
  officer_name?: string | null;
  created_by?: number | string | null;
  /** Explicit marker, when the case model carries one. */
  is_artifact?: boolean | null;
  tags?: string[] | null;
};

export type ArtifactContext = {
  /** The signed-in user's id; cases they created are never hidden. */
  currentUserId?: number | string | null;
  /** Ids of cases the user viewed in the last 30 days (see recentCaseIdsFrom). */
  recentCaseIds?: ReadonlySet<string>;
};

/** True when the case looks like an artifact by marker or title start alone (no ownership exemption). */
export function matchesArtifactMarker(caseLike: CaseLike): boolean {
  if (caseLike?.is_artifact === true) return true;
  if (Array.isArray(caseLike?.tags) && caseLike.tags.some((t) => ARTIFACT_TAGS.has(String(t).trim().toLowerCase()))) {
    return true;
  }
  return ARTIFACT_TITLE_START.test(String(caseLike?.title || ''));
}

export function isOwnedOrRecent(caseLike: CaseLike, ctx: ArtifactContext = {}): boolean {
  const { currentUserId, recentCaseIds } = ctx;
  if (currentUserId != null && caseLike?.created_by != null && String(caseLike.created_by) === String(currentUserId)) {
    return true;
  }
  return caseLike?.id != null && !!recentCaseIds?.has(String(caseLike.id));
}

export function isCaseArtifact(caseLike: CaseLike, ctx: ArtifactContext = {}): boolean {
  if (!matchesArtifactMarker(caseLike)) return false;
  return !isOwnedOrRecent(caseLike, ctx);
}

export function partitionCases<T extends CaseLike>(
  items: T[],
  hideArtifacts: boolean,
  ctx: ArtifactContext = {},
): { visible: T[]; hidden: T[] } {
  if (!hideArtifacts) return { visible: items, hidden: [] };
  const visible: T[] = [];
  const hidden: T[] = [];
  for (const item of items) (isCaseArtifact(item, ctx) ? hidden : visible).push(item);
  return { visible, hidden };
}

export function getVisibleCases<T extends CaseLike>(items: T[], hideArtifacts: boolean, ctx: ArtifactContext = {}): T[] {
  return partitionCases(items, hideArtifacts, ctx).visible;
}

/** Ids of cases viewed within the window, from Recently Viewed rows. */
export function recentCaseIdsFrom(
  rows: ReadonlyArray<{ resource_type?: string; resource_id?: string | number; last_viewed_at?: string }> | null | undefined,
  now: number = Date.now(),
): Set<string> {
  const cutoff = now - RECENT_VIEW_WINDOW_DAYS * 86_400_000;
  const ids = new Set<string>();
  for (const row of rows || []) {
    if (row?.resource_type !== 'case' || row.resource_id == null) continue;
    const t = row.last_viewed_at ? new Date(row.last_viewed_at).getTime() : NaN;
    if (!Number.isNaN(t) && t >= cutoff) ids.add(String(row.resource_id));
  }
  return ids;
}

/** The signed-in user's id as stored by the login flow, or null. */
export function currentUserId(): number | string | null {
  try {
    const raw = localStorage.getItem('user');
    if (!raw) return null;
    const id = JSON.parse(raw)?.id;
    return id == null ? null : id;
  } catch {
    return null;
  }
}

/** Persisted per browser. Default OFF: every case is shown. */
export function readHideArtifacts(): boolean {
  try {
    return localStorage.getItem(HIDE_ARTIFACTS_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

export function writeHideArtifacts(on: boolean): void {
  try {
    localStorage.setItem(HIDE_ARTIFACTS_STORAGE_KEY, on ? '1' : '0');
  } catch { /* storage unavailable: the choice lasts for this page only */ }
}
